import { createFileRoute, Outlet, redirect, Link, useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Toaster, toast } from "sonner";
import { Command } from "cmdk";
import {
  LayoutDashboard, Users, Briefcase, Kanban, CheckSquare, Ticket,
  Receipt, CreditCard, UserCog, Image as ImageIcon, LayoutTemplate,
  BarChart3, Settings, Zap, Search, Bell, LogOut, Menu, X,
  ChevronsLeft, Sparkles, PanelRightClose, PanelRightOpen, Sun,
} from "lucide-react";

export const Route = createFileRoute("/admin")({
  ssr: false,
  beforeLoad: async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) throw redirect({ to: "/auth" });
    const { data: role } = await supabase
      .from("user_roles").select("role").eq("user_id", data.user.id)
      .eq("role", "admin").maybeSingle();
    if (!role) throw redirect({ to: "/portal" });
    return { user: data.user };
  },
  component: AdminShell,
});

const NAV = [
  { section: "Overview", items: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  ]},
  { section: "CRM", items: [
    { to: "/admin/leads", label: "Leads", icon: Sparkles },
    { to: "/admin/clients", label: "Clients", icon: Users },
  ]},
  { section: "Delivery", items: [
    { to: "/admin/projects", label: "Projects", icon: Kanban },
    { to: "/admin/tasks", label: "Tasks", icon: CheckSquare },
    { to: "/admin/tickets", label: "Tickets", icon: Ticket },
  ]},
  { section: "Revenue", items: [
    { to: "/admin/invoices", label: "Invoices", icon: Receipt },
    { to: "/admin/subscriptions", label: "Subscriptions", icon: CreditCard },
  ]},
  { section: "Assets", items: [
    { to: "/admin/team", label: "Team", icon: UserCog },
    { to: "/admin/media", label: "Media Library", icon: ImageIcon },
    { to: "/admin/templates", label: "Templates", icon: LayoutTemplate },
  ]},
  { section: "Insights", items: [
    { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
    { to: "/admin/settings", label: "Settings", icon: Settings },
  ]},
];

function AdminShell() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(true);
  const [cmdOpen, setCmdOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const router = useRouter();
  const qc = useQueryClient();

  useEffect(() => setMobileOpen(false), [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setCmdOpen((v) => !v); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Realtime notifications toast
  useEffect(() => {
    const ch = supabase.channel("admin-realtime")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "leads" },
        (p) => toast.success(`🚀 Nuovo lead: ${(p.new as { name?: string })?.name ?? "—"}`))
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "tickets" },
        (p) => toast.info(`🎫 Nuovo ticket: ${(p.new as { title?: string })?.title ?? "—"}`))
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const signOut = async () => {
    await qc.cancelQueries(); qc.clear();
    await supabase.auth.signOut();
    await router.invalidate();
    navigate({ to: "/auth", replace: true });
  };

  const sidebarWidth = collapsed ? "lg:w-16" : "lg:w-64";

  return (
    <div className="min-h-screen bg-[#07070c] text-white/90 flex overflow-hidden">
      <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "rgba(15,15,25,0.9)", border: "1px solid rgba(255,255,255,0.1)", backdropFilter: "blur(20px)" } }} />

      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-1/4 left-1/4 h-[600px] w-[600px] rounded-full bg-fuchsia-600/5 blur-3xl" />
        <div className="absolute -bottom-1/4 right-1/4 h-[600px] w-[600px] rounded-full bg-cyan-500/5 blur-3xl" />
      </div>

      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex ${sidebarWidth} fixed inset-y-0 left-0 flex-col border-r border-white/[0.06] bg-black/40 backdrop-blur-2xl z-30 transition-all duration-300`}>
        <SidebarInner collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} onSignOut={signOut} />
      </aside>

      {/* Mobile sidebar */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 lg:hidden" />
            <motion.aside initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ type: "spring", damping: 26, stiffness: 240 }}
              className="fixed inset-y-0 left-0 w-72 bg-[#0a0a12] border-r border-white/10 z-50 lg:hidden flex flex-col">
              <SidebarInner collapsed={false} onSignOut={signOut} onClose={() => setMobileOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className={`flex-1 relative transition-all duration-300 ${collapsed ? "lg:pl-16" : "lg:pl-64"}`}>
        {/* Topbar */}
        <header className="sticky top-0 z-20 h-14 border-b border-white/[0.06] bg-[#07070c]/80 backdrop-blur-2xl px-3 lg:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 -ml-1 rounded-lg hover:bg-white/5">
              <Menu className="h-5 w-5" />
            </button>
            <button onClick={() => setCmdOpen(true)}
              className="hidden md:flex items-center gap-2 w-64 h-9 px-3 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-white/15 text-sm text-white/40 transition">
              <Search className="h-3.5 w-3.5" />
              <span>Search or run command…</span>
              <kbd className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 font-mono">⌘K</kbd>
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setRightOpen((v) => !v)} className="hidden xl:flex p-2 rounded-lg hover:bg-white/5 text-white/60" title="Toggle right panel">
              {rightOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
            </button>
            <NotifBell />
            <Link to="/portal" className="p-2 rounded-lg hover:bg-white/5 text-white/60 text-xs" title="Vista cliente">
              <Sun className="h-4 w-4" />
            </Link>
            <button onClick={signOut} className="p-2 rounded-lg hover:bg-white/5 text-white/60" title="Sign out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="flex">
          <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
            <AnimatePresence mode="wait">
              <motion.div key={location.pathname}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}>
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </main>
          {rightOpen && <RightRail />}
        </div>
      </div>

      {/* Command bar */}
      <CommandBar open={cmdOpen} onOpenChange={setCmdOpen} onNavigate={(to) => { setCmdOpen(false); navigate({ to }); }} />
    </div>
  );
}

function SidebarInner({ collapsed, onToggle, onClose, onSignOut }: { collapsed: boolean; onToggle?: () => void; onClose?: () => void; onSignOut: () => void }) {
  return (
    <>
      <div className="h-14 px-3 flex items-center justify-between border-b border-white/[0.06]">
        <Link to="/admin" className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 shrink-0 rounded-lg bg-gradient-to-br from-fuchsia-500 to-cyan-400 grid place-items-center shadow-lg shadow-fuchsia-500/20">
            <Zap className="h-4 w-4 text-black" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-semibold text-sm leading-tight truncate">FlashWeb</div>
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 leading-tight">Admin OS</div>
            </div>
          )}
        </Link>
        {onClose && <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5"><X className="h-4 w-4" /></button>}
        {onToggle && !collapsed && <button onClick={onToggle} className="p-1.5 rounded-lg hover:bg-white/5 text-white/40"><ChevronsLeft className="h-3.5 w-3.5" /></button>}
      </div>
      <nav className="flex-1 overflow-y-auto py-3">
        {NAV.map((group) => (
          <div key={group.section} className="mb-4">
            {!collapsed && <div className="px-4 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/30">{group.section}</div>}
            <div className="px-2 space-y-0.5">
              {group.items.map((item) => (
                <Link key={item.to} to={item.to} activeOptions={{ exact: (item as { exact?: boolean }).exact }}
                  title={collapsed ? item.label : undefined}
                  className={`group relative flex items-center gap-3 ${collapsed ? "justify-center px-2" : "px-3"} py-2 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/[0.04] transition [&.active]:text-white [&.active]:bg-gradient-to-r [&.active]:from-fuchsia-500/[0.12] [&.active]:to-cyan-400/[0.08] [&.active]:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]`}>
                  <item.icon className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </nav>
      {onToggle && collapsed && (
        <button onClick={onToggle} className="mx-2 mb-2 p-2 rounded-lg hover:bg-white/5 text-white/40 flex justify-center">
          <ChevronsLeft className="h-3.5 w-3.5 rotate-180" />
        </button>
      )}
      {!collapsed && (
        <div className="p-3 border-t border-white/[0.06]">
          <div className="rounded-xl p-3 bg-gradient-to-br from-white/[0.04] to-transparent border border-white/[0.06]">
            <div className="text-[10px] uppercase tracking-widest text-white/40">Loggato come</div>
            <div className="text-sm font-medium truncate">Admin</div>
            <button onClick={onSignOut} className="mt-2 w-full text-xs text-white/60 hover:text-white py-1.5 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] flex items-center justify-center gap-1.5">
              <LogOut className="h-3 w-3" /> Esci
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function NotifBell() {
  const { data: notifs } = useQuery({
    queryKey: ["admin-notif"],
    queryFn: async () => (await supabase.from("notifications").select("id, title, body, link, read_at, created_at").order("created_at", { ascending: false }).limit(8)).data ?? [],
    refetchInterval: 30_000,
  });
  const [open, setOpen] = useState(false);
  const unread = (notifs ?? []).filter((n) => !n.read_at).length;
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} className="relative p-2 rounded-lg hover:bg-white/5 text-white/60">
        <Bell className="h-4 w-4" />
        {unread > 0 && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-fuchsia-500 ring-2 ring-[#07070c] animate-pulse" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="absolute right-0 mt-2 w-80 rounded-2xl border border-white/10 bg-[#0f0f18]/95 backdrop-blur-2xl shadow-2xl overflow-hidden z-50">
            <div className="px-4 py-3 border-b border-white/5 text-sm font-semibold">Notifiche</div>
            <div className="max-h-96 overflow-y-auto">
              {(notifs ?? []).length === 0 && <div className="p-6 text-sm text-white/40 text-center">Nessuna notifica</div>}
              {(notifs ?? []).map((n) => (
                <div key={n.id} className="px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/[0.03]">
                  <div className="text-sm font-medium">{n.title}</div>
                  {n.body && <div className="text-xs text-white/50 mt-0.5">{n.body}</div>}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function RightRail() {
  const { data } = useQuery({
    queryKey: ["admin-rail"],
    queryFn: async () => {
      const [tickets, projects, leads] = await Promise.all([
        supabase.from("tickets").select("id, code, title, priority, status, created_at").in("priority", ["urgent", "high"]).not("status", "in", "(completed,closed)").order("created_at", { ascending: false }).limit(5),
        supabase.from("projects").select("id, name, delivery_date, progress").not("delivery_date", "is", null).order("delivery_date").limit(5),
        supabase.from("leads").select("id, name, company, created_at").eq("status", "new").order("created_at", { ascending: false }).limit(5),
      ]);
      return { tickets: tickets.data ?? [], projects: projects.data ?? [], leads: leads.data ?? [] };
    },
    refetchInterval: 60_000,
  });
  return (
    <aside className="hidden xl:block w-80 shrink-0 border-l border-white/[0.06] bg-black/20 p-4 space-y-4 max-h-[calc(100vh-3.5rem)] sticky top-14 overflow-y-auto">
      <RailBlock title="🔥 Ticket urgenti" empty="Nessun ticket urgente"
        items={(data?.tickets ?? []).map((t) => ({ id: t.id, title: t.title, sub: `${t.code} · ${t.priority.toUpperCase()}` }))} />
      <RailBlock title="📅 Prossime deadline" empty="Nessuna deadline vicina"
        items={(data?.projects ?? []).map((p) => ({ id: p.id, title: p.name, sub: p.delivery_date ? new Date(p.delivery_date).toLocaleDateString("it-IT") : "—" }))} />
      <RailBlock title="✨ Ultimi lead" empty="Nessun lead nuovo"
        items={(data?.leads ?? []).map((l) => ({ id: l.id, title: l.name, sub: l.company ?? "—" }))} />
    </aside>
  );
}

function RailBlock({ title, items, empty }: { title: string; items: { id: string; title: string; sub: string }[]; empty: string }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3">
      <div className="text-xs font-semibold text-white/70 mb-2">{title}</div>
      {items.length === 0 ? (
        <div className="text-xs text-white/30 py-2">{empty}</div>
      ) : (
        <div className="space-y-1.5">
          {items.map((it) => (
            <div key={it.id} className="text-xs p-2 rounded-lg hover:bg-white/[0.03] cursor-pointer">
              <div className="font-medium truncate">{it.title}</div>
              <div className="text-white/40 truncate mt-0.5">{it.sub}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CommandBar({ open, onOpenChange, onNavigate }: { open: boolean; onOpenChange: (v: boolean) => void; onNavigate: (to: string) => void }) {
  const items = NAV.flatMap((g) => g.items);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4" onClick={() => onOpenChange(false)}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
      <motion.div initial={{ opacity: 0, y: -12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-xl rounded-2xl border border-white/10 bg-[#0f0f18]/95 backdrop-blur-2xl shadow-2xl overflow-hidden">
        <Command className="[&_[cmdk-input]]:bg-transparent">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
            <Search className="h-4 w-4 text-white/40" />
            <Command.Input autoFocus placeholder="Search or run command…" className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/30" />
            <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 font-mono">ESC</kbd>
          </div>
          <Command.List className="max-h-96 overflow-y-auto p-2">
            <Command.Empty className="py-8 text-center text-sm text-white/40">Nessun risultato</Command.Empty>
            <Command.Group heading="Navigazione" className="[&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-widest [&_[cmdk-group-heading]]:text-white/40 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1">
              {items.map((item) => (
                <Command.Item key={item.to} onSelect={() => onNavigate(item.to)}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm cursor-pointer aria-selected:bg-white/[0.06]">
                  <item.icon className="h-4 w-4 text-white/50" />
                  <span>{item.label}</span>
                  <span className="ml-auto text-[10px] text-white/30">{item.to}</span>
                </Command.Item>
              ))}
            </Command.Group>
          </Command.List>
        </Command>
      </motion.div>
    </div>
  );
}
